import Axios, { AxiosRequestConfig } from "axios";
import {
  getLoginUrl,
  getStoredAccessToken,
  refreshToken,
  removeAllTokens,
} from "~/helpers";
import { useAuthStore } from "~/stores/authStore";

const axiosInstance = Axios.create();

// Flag to prevent multiple redirects
let isRedirecting = false;

// Paths that belong to AuthLayout where we should not perform token checks/redirects
const AUTH_PATH_PREFIXES = [
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/change-password",
  "/auth/callback",
  "/transcribing",
  "/share",
];

const isAuthRoute = (pathname: string) =>
  AUTH_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

axiosInstance.interceptors.request.use((config) => {
  const token = getStoredAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isOnAuthRoute =
      typeof location !== "undefined" && isAuthRoute(location.pathname);

    if (error?.response?.status === 401 && !originalRequest._retry) {
      // Do not attempt refresh or redirect when on AuthLayout routes
      if (isOnAuthRoute) {
        return Promise.reject(error);
      }
      originalRequest._retry = true;
      try {
        const newAccessToken = await refreshToken({ force: true });
        if (newAccessToken) {
          return axiosInstance(originalRequest);
        } else {
          removeAllTokens();
          const { setAuthExpired } = useAuthStore.getState();
          setAuthExpired(true);

          if (isOnAuthRoute || isRedirecting) {
            return Promise.reject(error);
          }

          isRedirecting = true;
          window.location.href = getLoginUrl();
        }
      } catch (refreshError) {
        removeAllTokens();
        const { setAuthExpired } = useAuthStore.getState();
        setAuthExpired(true);

        if (!isOnAuthRoute && !isRedirecting) {
          isRedirecting = true;
          window.location.href = getLoginUrl();
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Adds Authorization header (if token exists) and returns response data
export const customInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  return axiosInstance({
    ...config,
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
      ...options?.headers,
    },
  }).then(({ data }) => data as T);
};
